import { Injectable, OnDestroy } from '@angular/core';
import { LoggingService } from '@com-delta-idp/mnx-utils';
import { Observable, Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SFAFFeatureFlags } from '../../header/navigation-book/navigation-book.model';
import { TrackerDataService } from '../../services/acctactvty/trackerData.service';
import { CommonService } from '../../services/common.service';
import { Constants } from './constants';

@Injectable({
  providedIn: 'root'
})
export class FlightSearchUtils implements OnDestroy {
  private unsubscribe: Subject<void> = new Subject();

  constructor(
    private srv: CommonService,
    private constant: Constants,
    private loggingService: LoggingService,
    private trackerDataService: TrackerDataService
  ) {}

  redirectToSearchResult(
    data,
    cacheKeySuffixPass,
    isShoppingAwsFlow?: boolean,
    sfafFeatureFlags?: SFAFFeatureFlags,
    custId?: boolean,
    isEligibleBusinessUser?: boolean
  ) {
    let refUrl = null;
    if (data.flexAirport) {
      refUrl = '/flight-search/flexible-airports';
      if (isShoppingAwsFlow && sfafFeatureFlags?.enableSFAFFlexAirports) {
        refUrl = '/flightsearch/flexible-airports';
      }
    } else if (data.flexDate) {
      // Flex Dates Checkbox checked
      if (data.flexDaysWeeks === this.constant.FLEX_DAYS) {
        refUrl = '/flight-search/flexible-dates'; //old page
        if (
          isShoppingAwsFlow &&
          sfafFeatureFlags?.enableSFAFFlexDatesAndCalendar
        ) {
          if (
            !custId ||
            sfafFeatureFlags.enableSFAFFlexDatesAndCalendarWithLogin
          ) {
            refUrl = '/flightsearch/flexible-dates'; //new page
          }
        }
      } else if (data.flexDaysWeeks === this.constant.FLEX_WEEKS) {
        refUrl = '/flight-search/flexible-calendar';
        if (
          isShoppingAwsFlow &&
          sfafFeatureFlags?.enableSFAFFlexDatesAndCalendar
        ) {
          if (
            !custId ||
            sfafFeatureFlags.enableSFAFFlexDatesAndCalendarWithLogin
          ) {
            refUrl = '/flightsearch/flexible-calendar'; //new page
          }
        }
      }
    } else {
      if (isShoppingAwsFlow) {
        refUrl = '/flightsearch/search-results';
      } else {
        refUrl = '/flight-search/search-results';
      }
    }

    window.document.location.href = `${refUrl}?cacheKeySuffix=${
      cacheKeySuffixPass || null
    }`;
  }

  checkLocalStorageSupport() {
    const mod = 'data';
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(mod, mod);
        localStorage.removeItem(mod);
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  createSegments(searchData) {
    let segments = [];
    if (searchData.tripType === this.constant.TripType_MULTICITY) {
      const totalSegment =
        searchData && searchData.originCities
          ? searchData.originCities.length
          : 0;
      let indexkey = 0;
      for (indexkey = 0; indexkey < totalSegment; indexkey++) {
        if (searchData.originCities[indexkey] !== '') {
          segments.push({
            origin: searchData.originCities[indexkey],
            destination: searchData.destinationCities[indexkey],
            originCountryCode: searchData.originCountryCodes[indexkey],
            destinationCountryCode:
              searchData.destinationCountryCodes[indexkey],
            departureDate: this.formatDateString(
              searchData.departureDates[indexkey]
            ),
            returnDate: null,
            connectionAirportCode:
              searchData.connectionCity[indexkey] !== ''
                ? searchData.connectionCity[indexkey]
                : null
          });
        }
      }
    } else if (searchData.tripType === this.constant.TripType_ROUND_TRIP) {
      segments = [
        {
          origin: searchData.originCity,
          destination: searchData.destinationCity,
          originCountryCode: searchData.originCountryCode,
          destinationCountryCode: searchData.destinationCountryCode,
          departureDate: this.formatDateString(searchData.departureDate),
          returnDate: this.formatDateString(searchData.returnDate),
          connectionAirportCode: null
        }
      ];
    } else {
      segments = [
        {
          origin: searchData.originCity,
          destination: this.formatDateString(searchData.destinationCity),
          originCountryCode: searchData.originCountryCode,
          destinationCountryCode: searchData.destinationCountryCode,
          departureDate: this.formatDateString(searchData.departureDate),
          connectionAirportCode: null
        }
      ];
    }
    return segments;
  }

  formatDateString(date) {
    if (!date || !date.length) {
      this.loggingService.log('Error occured in Date format');
      return date;
    }
    if (date.indexOf('/') >= 0) {
      const dateObj = date.split('/');
      if (dateObj.length === 3) {
        const year = dateObj[2];
        const month = dateObj[0];
        const day = dateObj[1];
        return [year, month, day].join('-');
      } else {
        this.loggingService.log('Error occured in Date format');
      }
    }
    return date;
  }

  processSearch(
    searchData,
    cacheKey,
    deepLinkVendorId,
    isShoppingAwsFlow?: boolean,
    sfafFeatureFlags?: SFAFFeatureFlags,
    custId?: boolean,
    isEligibleBusinessUser?: boolean
  ) {
    deepLinkVendorId =
      this.srv.deepLinkVendorId !== ''
        ? this.srv.deepLinkVendorId
        : deepLinkVendorId;
    const flexDaysWeeks =
      (searchData.datesFlexible === 'true' ||
        searchData.datesFlexible === true) &&
      searchData.tripType !== this.constant.TripType_MULTICITY
        ? this.constant.FLEX_DAYS
        : '';
    let count = searchData.paxCount;
    if (searchData.adtCount !== undefined && searchData.adtCount > 0) {
      count = searchData.adtCount;
    }
    const passengerArray = [
      {
        count: count,
        type: 'ADT'
      }
    ];
    if (searchData.gbeCount !== undefined && searchData.gbeCount > 0) {
      passengerArray.push({
        count: searchData.gbeCount,
        type: 'GBE'
      });
    }
    if (searchData.childCount !== undefined && searchData.childCount > 0) {
      passengerArray.push({
        count: searchData.childCount,
        type: 'CNN'
      });
    }
    if (searchData.infantCount !== undefined && searchData.infantCount > 0) {
      passengerArray.push({
        count: searchData.infantCount,
        type: 'INF'
      });
    }
    const data = {
      request: {
        tripType: searchData.tripType,
        shopType:
          searchData.shopType === this.constant.REVENUE
            ? this.constant.ShopTypeMoney
            : this.constant.ShopTypeMiles,
        priceType:
          searchData.shopType === this.constant.REVENUE
            ? this.constant.PriceType_Revenue
            : this.constant.PriceType_Award,
        nonstopFlightsOnly: 'false',
        bookingPostVerify: searchData.bookingPostVerify,
        bundled: searchData.bundled,
        segments: this.createSegments(searchData),
        destinationAirportRadius: {
          measure: 100,
          unit: 'MI'
        },
        originAirportRadius: {
          measure: 100,
          unit: 'MI'
        },
        flexAirport:
          searchData.nearbyAirports === 'true' ||
          searchData.nearbyAirports === true,
        flexDate:
          (searchData.datesFlexible === 'true' ||
            searchData.datesFlexible === true) &&
          searchData.tripType !== this.constant.TripType_MULTICITY,
        flexDaysWeeks: flexDaysWeeks,
        passengers: passengerArray,
        meetingEventCode: searchData.meetingEventCode,
        bestFare: searchData.cabinFareClass,
        searchByCabin:
          searchData.searchByCabin === 'true' ||
          searchData.searchByCabin === true,
        cabinFareClass: null,
        refundableFlightsOnly:
          searchData.refundableFlightsOnly === 'on' ||
          searchData.refundableFlightsOnly === 'true' ||
          searchData.refundableFlightsOnly === true,
        deltaOnlySearch: searchData.deltaOnlySearch,
        initialSearchBy: {
          fareFamily: searchData.cabinFareClass,
          cabinFareClass: null,
          meetingEventCode: '',
          refundable: false,
          flexAirport: false,
          flexDate: false,
          flexDaysWeeks: '',
          deepLinkVendorId: deepLinkVendorId
        },
        searchType: searchData.searchType ? searchData.searchType : 'search',
        searchByFareClass: null,
        pageName: '',
        requestPageNum: '',
        action: searchData.action,
        actionType: '',
        priceSchedule: searchData.shopType,
        schedulePrice: searchData.schedulePrice,
        shopWithMiles: searchData.shopWithMiles ? 'on' : '',
        awardTravel: searchData.awardTravel,
        datesFlexible:
          (searchData.datesFlexible === 'true' ||
            searchData.datesFlexible === true) &&
          searchData.tripType !== this.constant.TripType_MULTICITY,
        flexCalendar: false,
        upgradeRequest: false,
        is_Flex_Search: true,
        includeBasicFaresUnchecked: searchData.includeBasicFaresUnchecked
      }
    };
    data.request.initialSearchBy = {
      fareFamily: data.request.bestFare,
      cabinFareClass: null,
      meetingEventCode: '',
      refundable: data.request.refundableFlightsOnly,
      flexAirport: data.request.flexAirport,
      flexDate:
        data.request.datesFlexible &&
        searchData.tripType !== this.constant.TripType_MULTICITY,
      flexDaysWeeks: '',
      deepLinkVendorId: deepLinkVendorId
    };
    if (data.request.flexAirport) {
      data.request.pageName = this.constant.FLEX_AIRPORT;
    } else if (data.request.datesFlexible) {
      data.request.pageName = this.constant.FLEX_DATE;
      if (flexDaysWeeks === this.constant.FLEX_DAYS) {
        data.request.pageName = this.constant.FLEX_DATE;
        data.request.initialSearchBy.flexDaysWeeks = this.constant.FLEX_DAYS;
      } else if (flexDaysWeeks === this.constant.FLEX_WEEKS) {
        data.request.pageName = this.constant.CALENDAR_SEARCH;
        data.request.initialSearchBy.flexDaysWeeks = this.constant.FLEX_WEEKS;
      }
    }
    // clear existing postData objects before setting one
    this.clearPostData();
    if (cacheKey !== '' && cacheKey !== undefined) {
      // Set data into local storage
      if (this.checkLocalStorageSupport()) {
        localStorage.setItem(
          `postData${cacheKey}`,
          JSON.stringify(data.request)
        );
      }
      this.srv.shoppingOptimization
        ? this.initiatePostSearchCall(
            data,
            cacheKey,
            isShoppingAwsFlow,
            sfafFeatureFlags,
            custId,
            isEligibleBusinessUser,
            searchData.tripType
          )
        : this.redirectToSearchResult(
            data.request,
            cacheKey,
            isShoppingAwsFlow,
            sfafFeatureFlags,
            custId,
            isEligibleBusinessUser
          );
    } else {
      // Set data into local storage
      this.srv.shoppingOptimization
        ? this.initiatePostSearchLookupCall(
            data,
            isShoppingAwsFlow,
            sfafFeatureFlags,
            custId,
            isEligibleBusinessUser,
            searchData.tripType
          )
        : this.initiateLookUpCall(
            data,
            isShoppingAwsFlow,
            sfafFeatureFlags,
            custId,
            isEligibleBusinessUser
          );
    }
  }

  initiateLookUpCall(
    data,
    isShoppingAwsFlow: boolean,
    sfafFeatureFlags: SFAFFeatureFlags,
    custId: boolean,
    isEligibleBusinessUser: boolean
  ) {
    this.srv
      .getDefaultsAndLookup()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((resdata) => {
        const cacheKeySuffix = resdata.searchCriteria.request.cacheKey;
        // Set data into local storage
        if (cacheKeySuffix !== undefined && this.checkLocalStorageSupport()) {
          localStorage.setItem(
            `postData${cacheKeySuffix}`,
            JSON.stringify(data.request)
          );
        }
        this.redirectToSearchResult(
          data.request,
          cacheKeySuffix,
          isShoppingAwsFlow,
          sfafFeatureFlags,
          custId,
          isEligibleBusinessUser
        );
      });
  }

  initiatePostSearchLookupCall(
    data,
    isShoppingAwsFlow: boolean,
    sfafFeatureFlags: SFAFFeatureFlags,
    custId: boolean,
    isEligibleBusinessUser: boolean,
    tripType: string
  ) {
    this.processSearchLookup(tripType)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: ([updateSearch, resdata]: any) => {
          const cacheKeySuffix = resdata?.searchCriteria?.request?.cacheKey;
          // Set data into local storage
          if (cacheKeySuffix !== undefined && this.checkLocalStorageSupport()) {
            localStorage.setItem(
              `postData${cacheKeySuffix}`,
              JSON.stringify(data.request)
            );
          }
          this.redirectToSearchResult(
            data.request,
            cacheKeySuffix,
            isShoppingAwsFlow,
            sfafFeatureFlags,
            custId,
            isEligibleBusinessUser
          );
        },
        error: (err) => this.loggingService.log(err)
      });
  }

  initiatePostSearchCall(
    data,
    cacheKey,
    isShoppingAwsFlow: boolean,
    sfafFeatureFlags: SFAFFeatureFlags,
    custId: boolean,
    isEligibleBusinessUser: boolean,
    tripType: string
  ) {
    this.getRequiredAPIs(tripType)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((updateSearch) => {
        //redirectToSearchResult triggered so can't able to capture success or err response of prefill/update search. That's why i moved redirectToSearchResult here
        this.redirectToSearchResult(
          data.request,
          cacheKey,
          isShoppingAwsFlow,
          sfafFeatureFlags,
          custId,
          isEligibleBusinessUser
        );
      });
  }

  processSearchLookup(tripType: string) {
    const requiredAPIs = [
      this.getRequiredAPIs(tripType),
      this.srv.getDefaultsAndLookup()
    ];
    return forkJoin(requiredAPIs);
  }

  getRequiredAPIs(selectedTripType: string): Observable<any> {
    if (selectedTripType != this.constant.TripType_MULTICITY) {
      const recentData = this.trackerDataService.recentData;
      return this.trackerDataService.postRecentSearches(recentData);
    } else if (selectedTripType == this.constant.TripType_MULTICITY) {
      const multiCityResponse = this.trackerDataService.multiCityResponse;
      return this.trackerDataService.postMultiCitiesSearches(multiCityResponse);
    }
  }

  clearPostData() {
    if (this.checkLocalStorageSupport()) {
      for (let ind = 0; ind < localStorage.length; ind++) {
        const key = localStorage.key(ind);
        if (key.indexOf('postData') === 0) {
          localStorage.removeItem(key);
          ind--;
        }
      }
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.unsubscribe.unsubscribe();
  }
}
